import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NewsletterSignUp from '../../common/Newsletter-sign';
import heroImage from '../../../assets/christmas25/festive.webp';
import grottoImage from '../../../assets/christmas25/grotto.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  goldLt:    '#F5E8C8',
  bronze:    '#8B5E3C',
  crimson:   '#8B1A1A',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(480px, 72vh, 780px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) { height: clamp(520px, 82svh, 700px); }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    rgba(16,29,64,0.94) 0%,
    rgba(16,29,64,0.62) 32%,
    rgba(16,29,64,0.18) 60%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 38rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
  margin-bottom: 1.75rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? C.gold : 'rgba(255,255,255,0.11)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)'};
`;

const HeroBtns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    a { width: 100%; text-align: center; }
  }
`;

const BtnPrimary = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const BtnGhost = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.65);
  background: rgba(255,255,255,0.07);
  color: #fff;
  text-decoration: none;
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: 640px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  margin: 0 0 clamp(1.5rem, 3vw, 2.25rem);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: clamp(1rem, 2vw, 1.4rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? C.navy : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201,150,60,0.1);
  border-radius: 10px;
  margin-bottom: 1rem;
  font-size: 1.4rem;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.65)' : C.muted};
`;

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  border-radius: 12px;
  overflow: hidden;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Step = styled.div`
  background: ${p => p.$bg};
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
  color: #fff;
`;

const StepNum = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 2.2rem;
  font-weight: 700;
  color: rgba(255,255,255,0.22);
  line-height: 1;
  margin-bottom: 0.7rem;
`;

const StepTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
`;

const StepBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.7);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.2rem, 2.5vw, 1.65rem);
`;

const InfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const ImgStrip = styled.div`
  border-radius: 14px;
  overflow: hidden;
  height: clamp(280px, 40vw, 420px);
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 30%;
    display: block;
  }
`;

const MidCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  margin-top: clamp(2rem, 4vw, 2.75rem);
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  background: ${C.parchment};
  border: 1px solid rgba(201,150,60,0.2);
`;

const MidCtaText = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.28rem);
  font-weight: 600;
  line-height: 1.35;
  color: ${C.navy};
  flex: 1;
  min-width: min(100%, 16rem);
`;

const MidCtaBtn = styled.a`
  flex-shrink: 0;
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;
  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
`;

const CtaBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.5);
  background: transparent;
  color: #fff;
  text-decoration: none;
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.32);
  letter-spacing: 0.06em;
`;

const MiceTrail = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>Mice Trail | Christmas 2026 at Belvoir Castle</title>
        <meta name="description" content="The Mice Trail returns for Christmas 2026! Follow the beloved Three Mice through Belvoir Castle's carol-themed grand rooms. Included free with every Deck the Halls entry ticket." />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/mice-trail" />
        <meta property="og:title" content="The Mice Trail — Christmas 2026 at Belvoir Castle" />
        <meta property="og:description" content="Follow the beloved Three Mice through the Castle's carol-themed rooms. Included free with every Deck the Halls entry ticket." />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/mice-trail" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Mice Trail — Christmas 2026 at Belvoir Castle" />
        <meta name="twitter:description" content="Follow the beloved Three Mice through the Castle's carol-themed rooms. Included free with every entry ticket." />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:site" content="@belvoircastle" />
      </Helmet>

      <HeroWrap>
        <HeroImg src={heroImage} alt="The Mice Trail at Belvoir Castle" />
        <HeroOverlay />
        <HeroContent>
          <HeroKicker>Returning for Christmas 2026 · Inside Belvoir Castle</HeroKicker>
          <HeroTitle>
            The <em>Mice Trail</em>
          </HeroTitle>
          <HeroStrap>
            Follow the beloved Three Mice through Belvoir Castle's grand carol-themed rooms
          </HeroStrap>
          <BadgeRow>
            <Pill $fill>Included with Entry</Pill>
            <Pill>Deck the Halls Experience</Pill>
            <Pill>Family-Friendly</Pill>
          </BadgeRow>
          <HeroBtns>
            <BtnPrimary href="/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761">
              Book Deck the Halls
            </BtnPrimary>
            <BtnGhost href="/christmas/your-festive-visit">
              Plan Your Visit
            </BtnGhost>
          </HeroBtns>
        </HeroContent>
      </HeroWrap>

      <Section $bg={C.ivory} $border>
        <Inner>
          <TwoCols $cols="1.1fr 0.9fr" $align="center">
            <div>
              <Kicker>The Trail</Kicker>
              <Divider />
              <SectionTitle>Three Mice.<br /><em>One Magical Castle.</em></SectionTitle>
              <SectionLead>
                Our beloved Three Mice are back for Christmas 2026! Spot them hiding in Belvoir Castle's grand rooms — each dressed as a different Christmas carol for Deck the Halls.
              </SectionLead>
              <SectionLead style={{ marginBottom: 0 }}>
                Pick up your trail sheet on arrival and follow the route through the Castle's extraordinary seasonal rooms. How many mice can your family find? A gentle, charming adventure that weaves through one of England's most spectacular Christmas settings.
              </SectionLead>
              <MidCta style={{ marginTop: '1.75rem' }}>
                <MidCtaText>Included free with every Deck the Halls entry ticket</MidCtaText>
                <MidCtaBtn href="/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761">Book Now</MidCtaBtn>
              </MidCta>
            </div>
            <ImgStrip>
              <img src={grottoImage} alt="Belvoir Castle Christmas" />
            </ImgStrip>
          </TwoCols>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>What to Expect</Kicker>
          <Divider />
          <SectionTitle>Everything About <em>the Trail</em></SectionTitle>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon>🐭</FeatureIcon>
              <FeatureTitle>The Three Mice</FeatureTitle>
              <FeatureBody>Our beloved Three Mice are tucked away throughout the Castle's grand rooms — each hiding in a different carol-themed space. Spot them all to complete the trail.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>📜</FeatureIcon>
              <FeatureTitle>Trail Sheet on Arrival</FeatureTitle>
              <FeatureBody>Pick up your trail sheet when you arrive at the Castle. No extra booking, no extra cost — just collect it at the entrance and start exploring.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🎶</FeatureIcon>
              <FeatureTitle>Through Deck the Halls</FeatureTitle>
              <FeatureBody>The trail takes you through every carol-themed room — from Deck the Halls to The Twelve Days of Christmas — making it the perfect way to explore the whole Castle.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>👨‍👩‍👧‍👦</FeatureIcon>
              <FeatureTitle>Perfect for All Ages</FeatureTitle>
              <FeatureBody>Designed especially for children but loved by the whole family. The trail adds a layer of discovery and fun to every room you visit.</FeatureBody>
            </FeatureCard>
            <FeatureCard $dark>
              <FeatureIcon>🎟️</FeatureIcon>
              <FeatureTitle $light>Free with Entry</FeatureTitle>
              <FeatureBody $light>No separate ticket, no extra booking. The Mice Trail is included as part of every Deck the Halls entry to Belvoir Castle. Simply book your entry and the trail is yours.</FeatureBody>
            </FeatureCard>
          </FeatureGrid>
        </Inner>
      </Section>

      <Section $bg={C.parchment} $border>
        <Inner>
          <Kicker>How It Works</Kicker>
          <Divider />
          <SectionTitle>Three Steps to <em>the Trail</em></SectionTitle>
          <StepGrid>
            <Step $bg={C.navy}>
              <StepNum>01</StepNum>
              <StepTitle>Book Your Entry</StepTitle>
              <StepBody>Book your Deck the Halls entry to Belvoir Castle online. Use our Family Saver system to get the best price for your group — the more you add, the more you save.</StepBody>
            </Step>
            <Step $bg={C.gold}>
              <StepNum>02</StepNum>
              <StepTitle>Collect Your Trail Sheet</StepTitle>
              <StepBody>On arrival, collect your Mice Trail sheet from the Castle entrance. No separate booking required — it's included with every Deck the Halls ticket.</StepBody>
            </Step>
            <Step $bg="#1a2d5a">
              <StepNum>03</StepNum>
              <StepTitle>Explore & Spot the Mice</StepTitle>
              <StepBody>Work your way through each grand carol-themed room and find where the Three Mice are hiding. Complete the trail and enjoy a magical day at Belvoir.</StepBody>
            </Step>
          </StepGrid>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>Plan Your Visit</Kicker>
          <Divider />
          <SectionTitle>Essential <em>Information</em></SectionTitle>
          <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <InfoValue>29 November –<br />31 December 2026</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Price</InfoLabel>
              <InfoValue>Included with<br />Entry Ticket</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>Inside<br />Belvoir Castle</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Trail Sheet</InfoLabel>
              <InfoValue>Collected<br />on Arrival</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Booking</InfoLabel>
              <InfoValue>Book Castle Entry<br />— trail included</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Address</InfoLabel>
              <InfoValue>Belvoir Castle<br />Grantham, NG32 1PE</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Inner>
      </Section>

      <div style={{ padding: '0 0 1rem' }}>
        <NewsletterSignUp tag="Christmas 2026" />
      </div>

      <CtaSection>
        <CtaInner>
          <CtaTitle>
            Book <em>Deck the Halls</em>
          </CtaTitle>
          <CtaBody>
            The Mice Trail is included with every Deck the Halls entry ticket. Book your Castle visit online — add Santa's Grotto, Festive Afternoon Tea or more to build your perfect Christmas package.
          </CtaBody>
          <CtaBtnRow>
            <CtaBtn href="/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761">
              Book Now
            </CtaBtn>
            <CtaSecondary to="/christmas/your-festive-visit">
              Build Your Package
            </CtaSecondary>
          </CtaBtnRow>
          <CtaMeta>
            Christmas 2026 · Belvoir Castle · Mice Trail included with every entry · Family Saver available
          </CtaMeta>
        </CtaInner>
      </CtaSection>
    </Page>
  );
};

export default MiceTrail;
